import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  ArrowForwardOutlined,
  CheckCircleOutline,
  GroupsOutlined,
  LocationOnOutlined,
  VerifiedOutlined,
} from '@mui/icons-material';
import { Box, Button, Container, Grid, Stack, Typography } from '@mui/material';

const values = [
  'Honest, straightforward property advice',
  'Personal support from a dedicated local team',
  'Sales, lettings and management under one roof',
];

const highlights = [
  { icon: <GroupsOutlined />, value: 'Family-led', label: 'Personal service from people who care' },
  { icon: <LocationOnOutlined />, value: 'Middlesbrough', label: 'Genuine local market knowledge' },
  { icon: <VerifiedOutlined />, value: 'Since 2019', label: 'Built on trust and integrity' },
];

export default function AboutSection() {
  return (
    <Box component="section" aria-labelledby="home-about-heading" sx={{ bgcolor: '#fff', py: { xs: 7, md: 12 }, overflow: 'hidden' }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 5, md: 8 }} alignItems="center">
          <Grid size={{ xs: 12, md: 5 }}>
            <Box sx={{ position: 'relative', pr: { md: 2 }, pb: { xs: 4, md: 5 } }}>
              <Box
                component="img"
                src="/media/site/management.jpg"
                alt="A professionally managed residential property"
                sx={{ width: '100%', height: { xs: 390, sm: 500, md: 590 }, display: 'block', objectFit: 'cover', borderRadius: 1.5 }}
              />
              <Box sx={{ position: 'absolute', left: { xs: 18, sm: 28 }, right: { xs: 0, md: -8 }, bottom: 0, p: { xs: 2.5, sm: 3 }, bgcolor: '#10234b', color: '#fff', borderRadius: 1.5, boxShadow: '0 20px 45px rgba(16,35,75,.24)' }}>
                <Typography variant="overline" sx={{ color: '#b8c7ed', letterSpacing: '.13em' }}>Our promise</Typography>
                <Typography sx={{ mt: .5, fontSize: { xs: '1.15rem', sm: '1.35rem' }, fontWeight: 750, lineHeight: 1.45 }}>
                  More than helping people move—we build relationships that last.
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid size={{ xs: 12, md: 7 }}>
            <Typography variant="overline" color="primary.main" sx={{ letterSpacing: '.15em', fontWeight: 800 }}>
              About MRM Lettings
            </Typography>
            <Typography id="home-about-heading" component="h2" sx={{ mt: 1.25, maxWidth: 700, color: '#101828', fontSize: { xs: '2.2rem', sm: '2.75rem', md: '3.45rem' }, lineHeight: 1.06, letterSpacing: '-.045em', fontWeight: 850 }}>
              Local knowledge. Personal service. Property done properly.
            </Typography>
            <Typography sx={{ mt: 2.5, maxWidth: 680, color: '#526070', fontSize: { xs: '1rem', md: '1.08rem' }, lineHeight: 1.8 }}>
              Founded in 2019 by Mohammad Rasheed Mahammad Riyal, MRM Lettings is a family-led company built on honest advice and genuine local knowledge. Mr Rasheed and his family remain actively involved, working alongside our Middlesbrough team to support buyers, sellers, landlords and tenants across the region.
            </Typography>

            <Stack spacing={1.35} sx={{ mt: 3 }}>
              {values.map((value) => (
                <Stack key={value} direction="row" spacing={1.25} alignItems="center">
                  <CheckCircleOutline sx={{ color: '#b3162d', fontSize: 21 }} />
                  <Typography sx={{ color: '#263448', fontWeight: 650 }}>{value}</Typography>
                </Stack>
              ))}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 4 }}>
              <Button component={RouterLink} to="/about" variant="contained" size="large" endIcon={<ArrowForwardOutlined />}>
                Read our story
              </Button>
              <Button component={RouterLink} to="/contact" variant="outlined" size="large">
                Meet the team
              </Button>
            </Stack>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 6, md: 9 }, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, border: '1px solid #e2e8f0', borderRadius: 2, overflow: 'hidden', bgcolor: '#f8fafc' }}>
          {highlights.map((item, index) => (
            <Stack key={item.value} direction="row" spacing={2} alignItems="center" sx={{ p: { xs: 2.5, md: 3 }, borderLeft: { sm: index ? '1px solid #e2e8f0' : 0 }, borderTop: { xs: index ? '1px solid #e2e8f0' : 0, sm: 0 } }}>
              <Box sx={{ width: 46, height: 46, flex: '0 0 auto', display: 'grid', placeItems: 'center', bgcolor: '#e8edf9', color: '#10234b', borderRadius: 1.5 }}>{item.icon}</Box>
              <Box>
                <Typography sx={{ color: '#10234b', fontSize: '1.05rem', fontWeight: 800 }}>{item.value}</Typography>
                <Typography variant="body2" sx={{ mt: .25, color: '#64748b', lineHeight: 1.45 }}>{item.label}</Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Container>
    </Box>
  );
}
